
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authSupabaseClient } from '@/integrations/supabase/authClient';
import { clearConfigCache } from '@/config/dynamicProxy';
import { useAuth } from '@/contexts/AuthContext';
import { FALLBACK_PROXY_CONFIG } from '@/utils/fallbackFetch';

interface ProxyConfig {
  [key: string]: string;
}

interface ProxyConfigContextType {
  config: ProxyConfig;
  loading: boolean;
  error: string | null;
  reloadConfig: () => Promise<void>;
  updateConfig: (newConfig: ProxyConfig) => Promise<boolean>;
  forceReload: () => Promise<void>;
}

const ProxyConfigContext = createContext<ProxyConfigContextType | undefined>(undefined);

// Значения по умолчанию для неаутентифицированных пользователей
const defaultConfig: ProxyConfig = {
  VITE_SUPABASE_PROXY_ENABLED: 'false',
  VITE_SUPABASE_PROXY_URL: '',
  VITE_SUPABASE_PROXY_TIMEOUT: '30000',
  VITE_SUPABASE_STORAGE_PROXY_ENABLED: 'false',
  VITE_SUPABASE_STORAGE_PROXY_URL: '',
  VITE_SUPABASE_STORAGE_PROXY_TIMEOUT: '30000',
  VITE_GOOGLE_MAPS_PROXY_ENABLED: 'false',
  VITE_GOOGLE_MAPS_PROXY_URL: '',
  VITE_GOOGLE_MAPS_PROXY_TIMEOUT: '15000',
  VITE_GEOCODING_PROXY_ENABLED: 'false',
  VITE_GEOCODING_PROXY_URL: '',
  VITE_GEOCODING_PROXY_TIMEOUT: '15000',
  VITE_MAKE_WEBHOOKS_PROXY_ENABLED: 'false',
  VITE_MAKE_WEBHOOKS_PROXY_URL: '',
  VITE_MAKE_WEBHOOKS_PROXY_TIMEOUT: '10000',
  VITE_WEBHOOKS_PROXY_ENABLED: 'false',
  VITE_WEBHOOKS_PROXY_URL: '',
  VITE_WEBHOOKS_PROXY_TIMEOUT: '10000'
};

export const ProxyConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAdmin, isLoading: authLoading } = useAuth();
  const [config, setConfig] = useState<ProxyConfig>(defaultConfig);
  const [loading, setLoading] = useState(true); // Начинаем с состояния загрузки
  const [error, setError] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    console.log('🔄 Начинаем загрузку конфигурации прокси...');
    
    // Если пользователь не аутентифицирован, используем значения по умолчанию
    if (!user) {
      console.log('🔄 Используем конфигурацию по умолчанию (пользователь не авторизован)');
      setConfig(defaultConfig);
      setError(null);
      setLoading(false);
      return;
    }

    // Если пользователь не администратор, используем резервную конфигурацию с включенным прокси
    if (!isAdmin) {
      console.log('🔄 Используем резервную конфигурацию прокси (пользователь не администратор)');
      setConfig(FALLBACK_PROXY_CONFIG);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Загружаем конфигурацию из базы данных с fallback...');
      
      // Используем клиент с fallback-логикой и коротким таймаутом
      const data = await authSupabaseClient.invokeFunctionWithFallback('get-env-variables');
      
      const envData = data?.variables || {};
      
      // Если данных нет или они пустые, используем резервную конфигурацию
      if (Object.keys(envData).length === 0) {
        console.log('🔄 Нет данных от сервера, используем резервную конфигурацию');
        setConfig(FALLBACK_PROXY_CONFIG);
      } else {
        // Объединяем полученные данные с резервной конфигурацией
        const mergedConfig = { ...FALLBACK_PROXY_CONFIG, ...envData };
        setConfig(mergedConfig);
        console.log('✅ Конфигурация прокси загружена:', Object.keys(mergedConfig));
      }
    } catch (error) {
      console.error('❌ Ошибка при загрузке конфигурации прокси:', error);
      setError(`Ошибка загрузки конфигурации: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
      
      // В случае ошибки используем резервную конфигурацию для администраторов
      console.log('🔄 Используем резервную конфигурацию из-за ошибки');
      setConfig(FALLBACK_PROXY_CONFIG);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  const forceReload = useCallback(async () => {
    console.log('🔄 Принудительная перезагрузка конфигурации прокси...');
    clearConfigCache();
    await loadConfig();
  }, [loadConfig]);

  const updateConfig = useCallback(async (newConfig: ProxyConfig): Promise<boolean> => {
    // Обновление конфигурации доступно только администраторам
    if (!user || !isAdmin) {
      console.error('❌ Обновление конфигурации доступно только администраторам');
      setError('Обновление конфигурации доступно только администраторам');
      return false;
    }

    try {
      console.log('📝 Начинаем обновление конфигурации прокси:', newConfig);
      setError(null);
      setLoading(true);
      
      // Используем клиент с fallback-логикой
      const data = await authSupabaseClient.invokeFunctionWithFallback('update-env-variables', {
        variables: newConfig
      });

      console.log('✅ Ответ от функции обновления:', data);

      // Принудительно очищаем кэш и перезагружаем конфигурацию
      clearConfigCache();
      
      // Даём время для применения изменений в базе
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await forceReload();
      
      console.log('✅ Конфигурация прокси успешно обновлена и применена');
      return true;
    } catch (error) {
      console.error('❌ Ошибка при обновлении конфигурации прокси:', error);
      setError(`Ошибка обновления: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin, forceReload]);

  useEffect(() => {
    // Загружаем конфигурацию только после завершения проверки аутентификации
    if (!authLoading) {
      console.log('🔄 Аутентификация завершена, загружаем конфигурацию прокси...');
      loadConfig();
    }
  }, [authLoading, loadConfig]);

  // Логируем изменения состояния для отладки
  useEffect(() => {
    console.log('📊 Состояние ProxyConfigContext:', {
      loading,
      error,
      configKeys: Object.keys(config),
      authLoading,
      user: !!user,
      isAdmin
    });
  }, [loading, error, config, authLoading, user, isAdmin]);

  const value: ProxyConfigContextType = {
    config,
    loading,
    error,
    reloadConfig: loadConfig,
    updateConfig,
    forceReload
  };

  return (
    <ProxyConfigContext.Provider value={value}>
      {children}
    </ProxyConfigContext.Provider>
  );
};

export const useProxyConfig = () => {
  const context = useContext(ProxyConfigContext);
  if (context === undefined) {
    throw new Error('useProxyConfig must be used within a ProxyConfigProvider');
  }
  return context;
};
